import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWeather } from '@/hooks/useWeather';
import { useRanchAlerts } from '@/hooks/useRanchAlerts';
import { useRanchEvents } from '@/hooks/useRanchEvents';
import { useNotices } from '@/hooks/useNotices';
import { useSearch } from '@/hooks/useSearch';
import { HeroBanner } from '@/components/HeroBanner';
import { WeatherInfographic } from '@/components/WeatherInfographic';
import { PlaceRowNav } from '@/components/PlaceRowNav';
import { OpenBadge } from '@/components/OpenBadge';
import { SearchIcon, ChevronRightIcon, AlertIcon } from '@/components/Icons';
import { formatDriveTime } from '@/lib/formatting';
import { pickCriticalHomeAlerts } from '@/lib/homeAlerts';
import {
  eventSourceLabel,
  eventsInNextFiveDays,
  formatEventDayLabel,
  formatEventTimeRange,
  shortEventLocation,
  THIS_WEEK_EXTRA_DAYS,
} from '@shared/ranch-feed';
import { takeMeHomeDirectionsUrl } from '@shared/take-me-home';

function SearchResults({ query }: { query: string }) {
  const { results, loading } = useSearch(query);
  if (!query.trim()) return null;
  return (
    <div className="card" style={{ marginTop: 12 }}>
      {loading && <p style={{ color: 'var(--color-ink-muted)' }}>Searching…</p>}
      {!loading && results.length === 0 && <p style={{ color: 'var(--color-ink-muted)' }}>No matches for "{query}".</p>}
      {results.map((r, i) => {
        if (r.type === 'action' && r.action) {
          return (
            <Link key={`a-${i}`} to={r.action.href} className="list-row">
              <span>{r.action.label}</span>
              <ChevronRightIcon />
            </Link>
          );
        }
        if (r.type === 'contact' && r.contact) {
          return (
            <a key={`c-${i}`} href={`tel:${r.contact.phone ?? ''}`} className="list-row">
              <span>
                {r.contact.name} · {r.contact.role}
              </span>
              <ChevronRightIcon />
            </a>
          );
        }
        if (r.type === 'place' && r.place) {
          return (
            <div key={`p-${i}`} className="list-row list-row--place">
              <Link to={`/places/${r.place.slug}`} className="list-row__body">
                {r.place.name}
                <br />
                <small style={{ color: 'var(--color-ink-muted)' }}>{formatDriveTime(r.place.estimated_drive_minutes)} away</small>
              </Link>
              <div className="list-row__actions">
                <OpenBadge state={r.placeOpenState ?? r.place.open_state} />
                <PlaceRowNav place={r.place} />
              </div>
            </div>
          );
        }
        if (r.type === 'house_info' && r.houseInfo) {
          return (
            <Link key={`h-${i}`} to="/rental#house" className="list-row">
              <span>
                <strong>{r.houseInfo.label}</strong>
                <br />
                <small style={{ color: 'var(--color-ink)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }}>
                  {r.houseInfo.value}
                </small>
              </span>
              <ChevronRightIcon />
            </Link>
          );
        }
        return null;
      })}
    </div>
  );
}

export function HomePage() {
  const [query, setQuery] = useState('');
  const { weather, error: weatherError } = useWeather();
  const { alerts: ranchAlerts } = useRanchAlerts();
  const { data: weekEvents, loading: weekEventsLoading } = useRanchEvents(THIS_WEEK_EXTRA_DAYS);
  const notices = useNotices();

  const nextFive = useMemo(() => {
    if (!weekEvents) return [];
    return eventsInNextFiveDays(weekEvents.events, weekEvents.today).slice(0, 5);
  }, [weekEvents]);

  const criticalAlerts = useMemo(
    () => pickCriticalHomeAlerts({ weather, ranchAlerts, notices }),
    [weather, ranchAlerts, notices],
  );

  return (
    <div className="home-page">
      <HeroBanner />

      <div className="pill-row" role="group" aria-label="Shortcuts">
        <a
          className="interest-chip"
          href={takeMeHomeDirectionsUrl()}
          target="_blank"
          rel="noreferrer"
        >
          Take me home
        </a>
      </div>

      {weatherError && (
        <div className="card">
          <p style={{ color: 'var(--color-ink-muted)' }}>Weather is temporarily unavailable.</p>
        </div>
      )}
      {!weatherError && !weather && (
        <div className="card">
          <div className="skeleton" style={{ height: 60 }} />
        </div>
      )}
      {weather && <WeatherInfographic weather={weather} />}

      <div className="hero-whats-on">
        <Link to="/events?filter=week" className="hero-whats-on__head">
          <span>What’s On · next 5 days</span>
          <ChevronRightIcon />
        </Link>
        {weekEventsLoading && !weekEvents && (
          <div className="skeleton" style={{ height: 44 }} />
        )}
        {!weekEventsLoading && nextFive.length === 0 && (
          <p className="hero-whats-on__empty">Nothing listed in the next 5 days.</p>
        )}
        {nextFive.map((e) => {
          const loc = shortEventLocation(e.location);
          const meta = [
            formatEventDayLabel(e.date, weekEvents!.today),
            formatEventTimeRange(e),
            eventSourceLabel(e.kind),
            loc,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Link
              key={e.id}
              to={`/events?filter=week#event-${e.id}`}
              className="hero-whats-on__row"
            >
              <strong>{e.title}</strong>
              <span className="hero-whats-on__meta">{meta}</span>
            </Link>
          );
        })}
      </div>

      {criticalAlerts.length > 0 && (
        <div className="home-alerts" role="status">
          {criticalAlerts.map((a) => {
            const inner = (
              <>
                <AlertIcon />
                <span>{a.title}</span>
              </>
            );
            if (a.href && a.external) {
              return (
                <a key={a.id} href={a.href} className="home-alerts__row" target="_blank" rel="noreferrer">
                  {inner}
                </a>
              );
            }
            if (a.href) {
              return (
                <Link key={a.id} to={a.href} className="home-alerts__row">
                  {inner}
                </Link>
              );
            }
            return (
              <div key={a.id} className="home-alerts__row">
                {inner}
              </div>
            );
          })}
        </div>
      )}

      <form
        className="home-search"
        onSubmit={(e) => {
          e.preventDefault();
        }}
        role="search"
      >
        <div className="search-box">
          <SearchIcon />
          <label htmlFor="home-search" className="visually-hidden">
            What do you need?
          </label>
          <input
            id="home-search"
            type="search"
            placeholder="What do you need?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            enterKeyHint="search"
          />
        </div>
      </form>
      <SearchResults query={query} />
    </div>
  );
}
